import {
  Box,
  Button,
  Divider,
  Icon,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert,
} from "@mui/material";
import { FC, useEffect, useRef, useState, useCallback } from "react";
import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import { useLocation, useNavigate } from "react-router-dom";
import { useRecoilState } from "recoil";
import openCenteredPopup from "../utils/urlUtil";
import {
  isMenuOpenState,
  sidebarScrollPositionState,
} from "../recoil/sideBarState";
import { MenuData } from "../types/menu";
import { getMenu } from "../api/menus";
import LogoutIcon from '@mui/icons-material/Logout';
import { PATHS } from "../routes/paths";
import useAuth from "../hooks/useAuth";
import { useAppDispatch } from "../store/hooks";
import { logout } from "../store/auth/slice";

export interface SidebarProps { }

const Sidebar: FC<SidebarProps> = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMenuOpen, setIsMenuOpen] = useRecoilState(isMenuOpenState);
  const [scrollPosition, setScrollPosition] = useRecoilState(
    sidebarScrollPositionState
  );
  const sidebarRef = useRef<HTMLDivElement>(null);
  const [menus, setMenus] = useState<MenuData>({ data: [] });
  const menuWidth = isMenuOpen ? "250px" : "100px";
  const isPhone = false; //useMediaQuery((theme: Theme) => theme.breakpoints.down("md"));
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [userToggled, setUserToggled] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const { signOut } = useAuth();
  const dispatch = useAppDispatch();
  const [logoutError, setLogoutError] = useState<string | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const fetchMenu = useCallback(async () => {
    try {
      const data = await getMenu(!isPhone ? "admin" : "user");
      setMenus(data);
    } catch (error) {
      console.error("Failed to fetch menu:", error);
    }
  }, [isPhone]);

  useEffect(() => {
    setIsMenuOpen(true);
  }, [isPhone, setIsMenuOpen]);

  useEffect(() => {
    if (menus.data.length === 0) {
      fetchMenu();
    }
    if (sidebarRef.current) {
      sidebarRef.current.scrollTop = scrollPosition;
    }
  }, [menus.data.length, scrollPosition, fetchMenu]);

  useEffect(() => {
    if (isMobile && !userToggled) {
      setIsMenuOpen(false);
    }
  }, [isMobile, userToggled, setIsMenuOpen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > theme.breakpoints.values.md && !userToggled) {
        setIsMenuOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [theme.breakpoints.values.md, userToggled, setIsMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
    setUserToggled(true);
  };

  const handleScroll = () => {
    if (sidebarRef.current) {
      setScrollPosition(sidebarRef.current.scrollTop);
    }
  };

  const openLinkInNewWindow = (url: string | URL | undefined) => {
    openCenteredPopup(url, 1200, 600);
  };

  const handleMenuClick = async (menu: any) => {
    if (menu.popup) {
      openLinkInNewWindow(menu.url);
    } else {
      navigate(menu.url);
    }
  };

  const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
    setLogoutError(null);
  };

  const handleLogout = async () => {
    try {
      const response = await signOut();

      if (response) {
        dispatch(logout());
        setAnchorEl(null);
        navigate(PATHS.AUTH_SIGN_IN);
        setLogoutError(null);
      } else {
        setLogoutError("ログアウトに失敗しました。再度お試しください。");
        setSnackbarOpen(true);
        console.error("ログアウトに失敗しました");
      }
    } catch (error: any) {
      setLogoutError(error.message || "ログアウト中にエラーが発生しました");
      setSnackbarOpen(true);
      console.error("ログアウトエラー:", error);
    }
  };

  const drawer = (
    <Box
      key={"sidebar"}
      ref={sidebarRef}
      onScroll={handleScroll}
      width="100%"
      padding="16px"
      sx={{
        maxHeight: "100vh",
        overflowY: "auto",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        display="flex"
        flexDirection={isMenuOpen ? "row" : "column"}
        justifyContent="space-between"
        alignItems="center"
        paddingX="8px"
        sx={{
          width: "100%",
        }}
      >
        <Box
          textAlign={{ sx: "left" }}
          flexGrow={1}
          alignItems={"center"}
          alignContent={"center"}
          justifyContent={isMenuOpen ? "flex-start" : "center"}
          display={"flex"}
          sx={{
            flexDirection: "column",
            alignItems: "start",
          }}
        >
          <Typography
            variant="h5"
            letterSpacing={1}
            fontWeight={800}
            fontFamily={'"Noto Serif JP", serif'}
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              flexGrow: isMenuOpen ? 1 : 0,
              opacity: isMenuOpen ? 1 : 0,
              visibility: isMenuOpen ? "visible" : "hidden",
              transition: isMenuOpen
                ? "max-width 0.3s ease 0.3s, opacity 0.2s ease 0.3s, visibility 0s ease 0.3s"
                : "max-width 0.3s ease, opacity 0.2s ease, visibility 0s ease 0.3s",
            }}
          >
            {isMenuOpen ? "OWReport" : ""}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            padding: 1,
            cursor: "pointer",
            alignSelf: isMenuOpen ? "center" : "center",
          }}
          onClick={toggleMenu}
        >
          {isMenuOpen ? (
            <KeyboardDoubleArrowLeftIcon sx={{ color: "white" }} />
          ) : (
            <KeyboardDoubleArrowRightIcon sx={{ color: "white" }} />
          )}
        </Box>
      </Box>

      <Divider
        sx={{ backgroundColor: "rgba(255, 255, 255, 0.40)", marginY: "10px" }}
      />
      {menus.data.map((group) => (
        <Box key={group.group_id}>
          <Typography
            textAlign={isMenuOpen ? "left" : "center"}
            variant="subtitle2"
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
              padding: "16px 8px 8px",
            }}
          >
            {group.group_name}
          </Typography>
          {group.menu.map((menu, menuIndex) => {
            const isSelected = location.pathname.startsWith(menu.url);
            return (
              <ListItem key={menuIndex} disablePadding>
                <Tooltip title={isMenuOpen ? "" : menu.title} placement="right">
                  <ListItemButton
                    onClick={() => handleMenuClick(menu)}
                    sx={{
                      borderRadius: "4px",
                      width: "100%",
                      justifyContent: isMenuOpen ? "flex-start" : "center",
                      padding: isMenuOpen ? "8px" : "8px 0",
                      backgroundColor: isSelected ? "primary.light" : "inherit",
                      "&:hover": {
                        backgroundColor: "primary.light",
                      },
                    }}
                  >
                    <ListItemIcon
                      sx={{
                        color: "white",
                        minWidth: isMenuOpen ? "40px" : 0,
                      }}
                    >
                      <Icon component={menu.icon}></Icon>
                    </ListItemIcon>
                    <ListItemText
                      primary={isMenuOpen ? menu.title : ""}
                      sx={{
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        flexGrow: isMenuOpen ? 1 : 0,
                        opacity: isMenuOpen ? 1 : 0,
                        visibility: isMenuOpen ? "visible" : "hidden",
                        transition: isMenuOpen
                          ? "max-width 0.3s ease 0.3s, opacity 0.2s ease 0.3s, visibility 0s ease 0.3s"
                          : "max-width 0.3s ease, opacity 0.2s ease, visibility 0s ease 0.3s",
                      }}
                    />
                    {isMenuOpen && menu.popup && <LaunchOutlinedIcon />}
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </Box>
      ))}

      <Box
        flexGrow={1}
        sx={{
          minHeight: 0,
        }}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: isMenuOpen ? "flex-start" : "center",
          padding: "10px 0",
        }}
      >
        <Button
          onClick={handleLogout}
          sx={{
            color: "white",
            textTransform: "none",
            fontWeight: "bold",
            padding: "8px 16px",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: "primary.light",
            },
          }}
          startIcon={<LogoutIcon />}
        >
          {isMenuOpen ? "ログアウト" : null}
        </Button>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
      >
        <List>
          <MenuItem onClick={handleClose}>
            <Typography>プロフィール</Typography>
          </MenuItem>
          <MenuItem onClick={handleLogout}>
            <Typography>ログアウト</Typography>
          </MenuItem>
        </List>
      </Popover>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="error"
          sx={{ width: '100%' }}
        >
          {logoutError}
        </Alert>
      </Snackbar>
    </Box>
  );

  return (
    <Box
      position={"relative"}
      component="nav"
      display="flex"
      flexShrink={0}
      flexGrow={0}
      width={menuWidth}
      borderRight={1}
      borderColor={"#E5E5E5"}
      sx={{
        transition: "width 0.3s ease",
        bgcolor: "primary.main",
        color: "white",
      }}
    >
      {drawer}
    </Box>
  );
};

export default Sidebar;